import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useMemo, useRef, Fragment } from 'react';
import { v4 as uuid } from 'uuid';
import { Advice, Icon, useTheme, createUseStyles } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import useOnboarding from "../../hooks/useOnboarding";
import { Modal, Table, EditForm } from "../";
import { types, TABLES, OPPORTUNITY_STATUS } from "../../../constants";
import styles from "./styles.js";
const useStyles = createUseStyles(styles, 'SetupPipeline');
var _ref4 = /*#__PURE__*/React.createElement(Icon, {
  name: "lock"
});
const SetupPipeline = _ref => {
  let {
    getList,
    setList,
    handleError,
    handleSetCount
  } = _ref;
  const modalInputRef = useRef(null);
  const {
    t
  } = useWeb5Context();
  const theme = useTheme();
  const classes = useStyles();
  const {
    state,
    dispatch,
    handleDragEnter,
    handleDragEnd,
    handleDelete,
    handleEdit,
    handleUpdate,
    formHasErrors,
    isRequired,
    validateFields
  } = useOnboarding({
    getData: () => getList(TABLES.OPPORTUNITY_STATUS),
    getDataCallback: data => data.sort((a, b) => a.order - b.order),
    handleError,
    handleSetCount: value => handleSetCount(TABLES.OPPORTUNITY_STATUS, value),
    setData: data => setList(TABLES.OPPORTUNITY_STATUS, data),
    parseData: data => data.map((item, index) => _objectSpread(_objectSpread({}, item), {}, {
      order: index
    }))
  });
  const pipelineStatesCount = useMemo(() => state.data.reduce((totals, item) => ({
    [OPPORTUNITY_STATUS.WON]: item.won ? [...totals[OPPORTUNITY_STATUS.WON], item.id] : totals[OPPORTUNITY_STATUS.WON],
    [OPPORTUNITY_STATUS.LOST]: item.endStatus && !item.won ? [...totals[OPPORTUNITY_STATUS.LOST], item.id] : totals[OPPORTUNITY_STATUS.LOST],
    [OPPORTUNITY_STATUS.OPEN]: !item.won && !item.endStatus ? [...totals[OPPORTUNITY_STATUS.OPEN], item.id] : totals[OPPORTUNITY_STATUS.OPEN]
  }), {
    [OPPORTUNITY_STATUS.WON]: [],
    [OPPORTUNITY_STATUS.LOST]: [],
    [OPPORTUNITY_STATUS.OPEN]: []
  }), [state.data]);
  const openStatusOption = useMemo(() => ({
    label: t('cfm_label_satate_open'),
    value: OPPORTUNITY_STATUS.OPEN,
    count: pipelineStatesCount[OPPORTUNITY_STATUS.OPEN].length,
    adviceCount: t('cfm_label_mandatory_1_state_open')
  }), [t, pipelineStatesCount]);
  const wonStatusOption = useMemo(() => ({
    label: t('cfm_label_satate_closed_won'),
    value: OPPORTUNITY_STATUS.WON,
    count: pipelineStatesCount[OPPORTUNITY_STATUS.WON].length,
    advice: t('label_probability_100'),
    adviceCount: t('cfm_label_mandatory_1_state_won')
  }), [t, pipelineStatesCount]);
  const lostStatusOption = useMemo(() => ({
    label: t('cfm_label_satate_closed_lost'),
    value: OPPORTUNITY_STATUS.LOST,
    count: pipelineStatesCount[OPPORTUNITY_STATUS.LOST].length,
    advice: t('label_probability_0'),
    adviceCount: t('cfm_label_mandatory_1_state_lost')
  }), [t, pipelineStatesCount]);
  const statusOptions = useMemo(() => [openStatusOption, wonStatusOption, lostStatusOption], [lostStatusOption, openStatusOption, wonStatusOption]);
  const getStatus = useCallback((won, endStatus) => {
    switch (true) {
      case won && endStatus:
        return wonStatusOption;
      case !won && endStatus:
        return lostStatusOption;
      default:
        return openStatusOption;
    }
  }, [lostStatusOption, openStatusOption, wonStatusOption]);
  const setStatus = useCallback((value, salesProbability) => {
    switch (value) {
      case OPPORTUNITY_STATUS.OPEN:
        return {
          won: false,
          endStatus: false,
          salesProbability
        };
      case OPPORTUNITY_STATUS.WON:
        return {
          won: true,
          endStatus: true,
          salesProbability: 100
        };
      case OPPORTUNITY_STATUS.LOST:
        return {
          won: false,
          endStatus: true,
          salesProbability: 0
        };
      default:
        break;
    }
  }, []);
  const getHints = useMemo(() => {
    const modalType = state.modal.type;
    const modal = state.modal[modalType];
    const {
      id,
      status: {
        value,
        advice,
        adviceCount,
        count
      } = {}
    } = (modal === null || modal === void 0 ? void 0 : modal.values) || {};
    return {
      advice,
      adviceCount: count === 1 && pipelineStatesCount[value].includes(id) ? adviceCount : undefined
    };
  }, [pipelineStatesCount, state.modal]);
  const formSchema = useMemo(() => {
    const {
      advice,
      adviceCount
    } = getHints;
    const hasAdvice = !!advice;
    const isReadOnly = !!adviceCount;
    return [{
      label: t('label_opportunity_status'),
      placeholder: t('placeholder_text_field'),
      labelMode: 'vertical',
      name: 'name',
      type: 'text',
      isFullWidth: true,
      isRequired: true,
      ref: ref => modalInputRef.current = ref
    }, {
      label: t('cfm_label_state_open_or_close'),
      placeholder: t('label_selectone'),
      labelMode: 'vertical',
      name: 'status',
      type: 'select',
      isReadOnly,
      isClearable: false,
      isRequired: true,
      attrs: {
        options: statusOptions,
        overrides: {
          controlFocused: {
            style: {
              border: `1px solid ${theme.colors.actionMajor[500]}`
            }
          }
        }
      }
    }, {
      label: t('label_sales_probability'),
      labelMode: 'vertical',
      name: 'salesProbability',
      type: 'slider',
      isPercentage: true,
      isReadOnly: isReadOnly || hasAdvice,
      step: 10,
      attrs: {
        overrides: {
          'rc-slider': {
            style: {
              minHeight: 40,
              padding: '24px 0 16px'
            }
          },
          overlayLabel: {
            className: classes.overlayLabel
          }
        }
      }
    }];
  }, [getHints, t, statusOptions, theme.colors.actionMajor, classes.overlayLabel]);
  const validateField = useCallback((field, value) => {
    let error = null;
    switch (field) {
      case 'name':
        error = isRequired(value);
        break;
      case 'status':
        error = isRequired(value);
        break;
      default:
        break;
    }
    return error;
  }, [isRequired]);
  const handleEditFormSave = useCallback(() => {
    const {
      id,
      name,
      status,
      salesProbability
    } = state.modal[types.MODAL_EDIT].values;
    const currentStates = state.data;
    const newStates = currentStates.reduce((arr, state) => {
      let newState;
      if (state.id === id) {
        newState = _objectSpread(_objectSpread({}, state), {}, {
          name
        }, setStatus(status.value, salesProbability));
      } else {
        newState = state;
      }
      return [...arr, newState];
    }, []);
    handleUpdate(newStates);
    dispatch({
      type: types.CLOSE_MODAL
    });
  }, [state.modal, state.data, handleUpdate, dispatch, setStatus]);
  const handleEditFormBlur = useCallback(_ref2 => {
    let {
      field
    } = _ref2;
    const modalType = state.modal.type;
    const form = state.modal[modalType];
    dispatch({
      type: types.UPDATE_MODAL,
      value: modalType,
      payload: {
        touched: _objectSpread(_objectSpread({}, form.touched), {}, {
          [field]: true
        })
      }
    });
  }, [dispatch, state.modal]);
  const handleEditFormChange = useCallback(_ref3 => {
    let {
      field,
      value
    } = _ref3;
    const modalType = state.modal.type;
    const form = state.modal[modalType];
    const error = validateField(field, value);
    const values = _objectSpread(_objectSpread({}, form.values), {}, {
      [field]: value
    });
    if (field === 'status') {
      if (value.value === OPPORTUNITY_STATUS.WON) values.salesProbability = 100;
      if (value.value === OPPORTUNITY_STATUS.LOST) values.salesProbability = 0;
    }
    dispatch({
      type: types.UPDATE_MODAL,
      value: modalType,
      payload: {
        values,
        errors: _objectSpread(_objectSpread({}, form.errors), {}, {
          [field]: error
        }),
        touched: _objectSpread(_objectSpread({}, form.touched), {}, {
          [field]: true
        })
      }
    });
  }, [dispatch, state.modal, validateField]);
  const handleAddNew = useCallback(() => {
    const values = state.modal[types.MODAL_ADD_SINGLE].values;
    const errors = validateFields(values, validateField, formSchema);
    const touched = Object.keys(errors).reduce((obj, key) => _objectSpread(_objectSpread({}, obj), {}, {
      [key]: true
    }), {});
    if (Object.values(errors).filter(Boolean).length === 0) {
      const {
        name,
        status,
        salesProbability
      } = values;
      const currentStates = state.data;
      const newStates = [...currentStates, _objectSpread({
        name,
        temp: true,
        id: uuid()
      }, setStatus(status.value, salesProbability))];
      handleUpdate(newStates);
      dispatch({
        type: types.CLOSE_MODAL
      });
    } else {
      dispatch({
        type: types.UPDATE_MODAL,
        value: types.MODAL_ADD_SINGLE,
        payload: {
          values,
          errors,
          touched
        }
      });
    }
  }, [dispatch, formSchema, handleUpdate, setStatus, state.data, state.modal, validateField, validateFields]);
  const getInfoForTag = useCallback(data => {
    if (!data) return null;
    const {
      endStatus,
      won,
      salesProbability
    } = data;
    let type = undefined;
    let literal = undefined;
    switch (true) {
      case won:
        type = 'success';
        literal = 'cfm_label_satate_closed_won';
        break;
      case endStatus:
        type = 'error';
        literal = 'cfm_label_satate_closed_lost';
        break;
      case !won && !endStatus:
        literal = 'cfm_label_satate_open';
        break;
      default:
        break;
    }
    return {
      type,
      value: `${t(literal)} - ${salesProbability}%`
    };
  }, [t]);
  const tableProps = useMemo(() => {
    return {
      title: t('label_opportunities_states_title'),
      description: t('label_opportunity_states_title_desc'),
      actionText: t('action_add_states'),
      actionOnClick: () => dispatch({
        type: types.OPEN_MODAL,
        value: types.MODAL_ADD_SINGLE
      }),
      columnDefs: [{
        colId: 'name',
        headerName: t('label_opportunity_status'),
        field: 'name',
        suppressSizeToFit: false,
        resizable: false,
        rowDrag: true
      }, {
        colId: 'salesProbability',
        headerName: t('label_sales_probability'),
        field: 'salesProbability',
        suppressSizeToFit: false,
        resizable: false,
        cellRenderer: 'tagCell',
        cellRendererParams: {
          align: 'right',
          innerMethods: {
            getInfoForTag
          }
        }
      }],
      rowData: state.data,
      actions: [row => {
        return !row.readOnly ? {
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "edit",
            onClick: () => handleEdit({
              id: row.id,
              name: row.name,
              status: getStatus(row.won, row.endStatus),
              salesProbability: row.salesProbability
            })
          }),
          tooltip: {
            content: t('action_edit')
          }
        } : null;
      }, row => {
        return !row.readOnly && !row.won && !row.endStatus && pipelineStatesCount[OPPORTUNITY_STATUS.OPEN].length > 1 || !row.readOnly && row.won && pipelineStatesCount[OPPORTUNITY_STATUS.WON].length > 1 || !row.readOnly && row.endStatus && !row.won && pipelineStatesCount[OPPORTUNITY_STATUS.LOST].length > 1 ? {
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "trash",
            onClick: () => handleDelete(row)
          }),
          tooltip: {
            content: t('action_delete')
          }
        } : null;
      }, row => {
        return row.readOnly ? {
          icon: _ref4,
          tooltip: {
            content: t('cfm_label_readonly')
          }
        } : null;
      }],
      loading: state.loading,
      useDragRows: true,
      getRowNodeId: row => row === null || row === void 0 ? void 0 : row.id,
      onDragEnter: handleDragEnter,
      onDragEnd: handleDragEnd,
      forceActionsRefresh: true
    };
  }, [t, getInfoForTag, state.data, state.loading, handleDragEnter, handleDragEnd, handleEdit, getStatus, pipelineStatesCount, handleDelete, dispatch]);
  const getPreComponent = useMemo(() => {
    const {
      advice,
      adviceCount
    } = getHints;
    return (advice || adviceCount) && /*#__PURE__*/React.createElement(Advice, {
      type: "info",
      showIcon: true,
      showCollapse: false,
      overrides: {
        Text: {
          style: {
            display: 'flex',
            flexFlow: 'column nowrap'
          }
        }
      }
    }, advice && /*#__PURE__*/React.createElement("span", {
      key: "advice-0"
    }, advice), adviceCount && /*#__PURE__*/React.createElement("span", {
      key: "advice-1"
    }, adviceCount));
  }, [getHints]);
  const modalProps = useMemo(() => {
    let customProps = {};
    const commonProps = {
      isOpen: state.modal.isOpen,
      onRequestClose: () => dispatch({
        type: types.CLOSE_MODAL
      }),
      onCancel: () => dispatch({
        type: types.CLOSE_MODAL
      }),
      modalInputRef: modalInputRef,
      preComponent: getPreComponent
    };
    switch (state.modal.type) {
      case types.MODAL_EDIT:
        customProps = {
          title: t('labe_edit_entity', [t('label_opportunity_status')]),
          onConfirm: handleEditFormSave,
          isConfirmDisabled: formHasErrors(types.MODAL_EDIT)
        };
        break;
      case types.MODAL_ADD_SINGLE:
        customProps = {
          title: t('label_opportunities_states_title'),
          description: t('label_opportunities_states_modal_desc'),
          onConfirm: handleAddNew,
          isConfirmDisabled: formHasErrors(types.MODAL_ADD_SINGLE)
        };
        break;
      default:
        break;
    }
    return _objectSpread(_objectSpread({}, commonProps), customProps);
  }, [dispatch, formHasErrors, handleEditFormSave, handleAddNew, state.modal, t, getPreComponent]);
  const modalContent = useMemo(() => {
    const modalType = state.modal.type;
    const modalContentProps = {
      form: state.modal[modalType],
      onChange: handleEditFormChange,
      onBlur: handleEditFormBlur,
      schema: formSchema,
      modalInputRef
    };
    return /*#__PURE__*/React.createElement(EditForm, modalContentProps);
  }, [state, handleEditFormChange, handleEditFormBlur, formSchema]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Table, tableProps), /*#__PURE__*/React.createElement(Modal, modalProps, modalContent));
};
export default /*#__PURE__*/memo(SetupPipeline);